/**
 * StaffSync — IT-SCE Intern: ERPNext-Sync-Status + letzte Sync-Vorgänge.
 *
 * TQ #9697 (Staff-Migration Teil D, Bundle #9523). Portiert aus
 * customer-portal/src/pages/staff/StaffSync.tsx — gleiche API-Calls
 * (api.staffGetSyncStatus/staffGetSyncStats/staffGetSyncRecent/
 * staffTriggerSyncBatch).
 *
 * Routing: outer-router-level (App.tsx) unter /staff/sync, hinter
 * StaffOnly-Guard, gerendert über StaffHealthHub (Tab "ERP-Sync").
 */

import { useEffect, useState, useCallback } from 'react'
import { api, SyncEntry } from '../../services/api'

type SyncStatus = { status: string; services: { database: string; erpnext: string }; version?: string; timestamp?: string }
type SyncStats = { total: number; successful: number; failed: number; pending: number; not_synced: number }

function StatusDot({ status }: { status: string }) {
  const color = status === 'ok' || status === 'healthy' ? '#10b981' : status === 'degraded' ? '#f59e0b' : '#ef4444'
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 6 }} />
}

export default function StaffSync() {
  const [status, setStatus] = useState<SyncStatus | null>(null)
  const [stats, setStats] = useState<SyncStats | null>(null)
  const [recent, setRecent] = useState<SyncEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [triggering, setTriggering] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [statusRes, statsRes, recentRes] = await Promise.allSettled([
        api.staffGetSyncStatus(),
        api.staffGetSyncStats(),
        api.staffGetSyncRecent(30),
      ])
      if (statusRes.status === 'fulfilled') setStatus(statusRes.value)
      if (statsRes.status === 'fulfilled') setStats(statsRes.value)
      if (recentRes.status === 'fulfilled') setRecent(recentRes.value.syncs ?? [])
    } catch {
      setError('Sync-Daten konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function triggerBatch() {
    if (triggering) return
    setTriggering(true)
    try {
      await api.staffTriggerSyncBatch()
      setTimeout(load, 2000)
    } catch {
      setError('Batch-Sync konnte nicht gestartet werden')
    } finally {
      setTriggering(false)
    }
  }

  if (loading) return <div style={{ padding: 24, color: '#6b7280' }}>Lade Sync-Status…</div>

  return (
    <div>
      {error && (
        <div style={{ padding: '10px 14px', marginBottom: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: '0.875rem' }}>
          {error}
        </div>
      )}

      {/* Status Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>Service Status</div>
          <div style={{ fontWeight: 600 }}>
            <StatusDot status={status?.status ?? 'unknown'} />
            {status?.status ?? '–'}
          </div>
          {status?.services && (
            <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#6b7280' }}>
              DB: {status.services.database} · ERP: {status.services.erpnext}
            </div>
          )}
        </div>
        {stats && (
          <>
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>Gesamt</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{stats.total}</div>
            </div>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: '0.75rem', color: '#16a34a', marginBottom: 4 }}>Erfolgreich</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#16a34a' }}>{stats.successful}</div>
            </div>
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: '0.75rem', color: '#dc2626', marginBottom: 4 }}>Fehler</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc2626' }}>{stats.failed}</div>
            </div>
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: '0.75rem', color: '#d97706', marginBottom: 4 }}>Ausstehend</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#d97706' }}>{stats.pending}</div>
            </div>
          </>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <button
          onClick={triggerBatch}
          disabled={triggering}
          style={{ padding: '8px 16px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, cursor: triggering ? 'default' : 'pointer', opacity: triggering ? 0.7 : 1, fontSize: '0.875rem' }}
        >
          {triggering ? 'Starte…' : '▶ Batch-Sync starten'}
        </button>
        <button
          onClick={load}
          style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: '0.875rem' }}
        >
          Aktualisieren
        </button>
      </div>

      {/* Recent Syncs Table */}
      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: 10 }}>
        Letzte Sync-Vorgänge
      </div>
      {recent.length === 0 ? (
        <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Keine Sync-Einträge vorhanden.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                {['ID', 'Typ', 'Entity', 'Status', 'Fehler', 'Erstellt'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.map(s => (
                <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '7px 12px', color: '#9ca3af' }}>{s.id}</td>
                  <td style={{ padding: '7px 12px' }}>{s.entity_type}</td>
                  <td style={{ padding: '7px 12px' }}>{s.entity_name ?? s.entity_id}</td>
                  <td style={{ padding: '7px 12px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
                      background: s.status === 'success' ? '#d1fae5' : s.status === 'failed' ? '#fee2e2' : '#fef3c7',
                      color: s.status === 'success' ? '#065f46' : s.status === 'failed' ? '#b91c1c' : '#92400e',
                    }}>
                      {s.status}
                    </span>
                  </td>
                  <td style={{ padding: '7px 12px', color: '#ef4444', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.error_message ?? '–'}
                  </td>
                  <td style={{ padding: '7px 12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {new Date(s.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
